import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { suppliers as mockSuppliers } from '../data/suppliers';

export interface Supplier {
  id: string;
  organization: string;
  representative: string;
  address: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  products: string;
  slug: string;
  subdomain: string | null;
  verified: boolean;
  year_established: number | null;
  rating: number;
  total_products: number;
  description: string;
  specialties: string[];
  location: string;
  city: string;
}

export const useSuppliers = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setLoading(true);
        
        // Get deleted suppliers from localStorage
        const deletedSuppliers = JSON.parse(localStorage.getItem('deletedSuppliers') || '[]');
        
        // Try to fetch from database first
        try {
          const { data, error: suppliersError } = await supabase
            .from('suppliers')
            .select('*')
            .order('organization');
          
          if (suppliersError) throw suppliersError;
          
          if (data && data.length > 0) {
            // Filter out deleted suppliers
            const filteredData = data.filter(supplier => !deletedSuppliers.includes(supplier.id));
            setSuppliers(filteredData);
          } else {
            // Use mock data if no database suppliers
            const filteredMockSuppliers = mockSuppliers.filter(supplier => !deletedSuppliers.includes(supplier.id));
            setSuppliers(filteredMockSuppliers);
          }
        } catch (dbError) {
          console.warn('Database not available, using mock data:', dbError);
          // Filter out deleted suppliers from mock data
          const filteredMockSuppliers = mockSuppliers.filter(supplier => !deletedSuppliers.includes(supplier.id));
          setSuppliers(filteredMockSuppliers);
        }
      } catch (err) {
        console.error('Error fetching suppliers:', err);
        // Fallback to mock data on any error
        const deletedSuppliers = JSON.parse(localStorage.getItem('deletedSuppliers') || '[]');
        const filteredMockSuppliers = mockSuppliers.filter(supplier => !deletedSuppliers.includes(supplier.id));
        setSuppliers(filteredMockSuppliers);
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, []);

  // Add function to delete supplier
  const deleteSupplier = (supplierId: string) => {
    // Remove from current state
    setSuppliers(prev => prev.filter(s => s.id !== supplierId));
    
    // Store in localStorage for persistence
    const deletedSuppliers = JSON.parse(localStorage.getItem('deletedSuppliers') || '[]');
    if (!deletedSuppliers.includes(supplierId)) {
      deletedSuppliers.push(supplierId);
      localStorage.setItem('deletedSuppliers', JSON.stringify(deletedSuppliers));
    }
  };
  const getSupplierBySlug = (slug: string): Supplier | undefined => {
    return suppliers.find(supplier => supplier.slug === slug);
  };

  const getSuppliersByCity = (city: string): Supplier[] => {
    return suppliers.filter(supplier => supplier.city.toLowerCase() === city.toLowerCase());
  };

  const getSuppliersByProduct = (productKeyword: string): Supplier[] => {
    return suppliers.filter(supplier => 
      supplier.products.toLowerCase().includes(productKeyword.toLowerCase()) ||
      supplier.specialties.some(specialty => 
        specialty.toLowerCase().includes(productKeyword.toLowerCase())
      )
    );
  };

  return { 
    suppliers, 
    loading, 
    error,
    deleteSupplier,
    getSupplierBySlug,
    getSuppliersByCity,
    getSuppliersByProduct
  };
};